<script>
import palette from '@inscribe/signal/dist/palette';
import DataBrick from '@/V2/analytics/DataBrick';
import DoughnutChart from '@/V2/analytics/DoughnutChart';
import TrustScoreDistribution from '@/V2/analytics/TrustScoreDistribution';
import { getDocumentTypeText } from '@/utils/documentType';
import { getFileTypeText } from '@/utils/fileType';
import { getPaletteColor } from './utils';
import {
  getAndSyncQueryParams,
  navigateWithQueryParams,
} from '@/utils/queryParams';
import { iIcon } from '@inscribe/signal';

export default {
  components: { DataBrick, iIcon, DoughnutChart, TrustScoreDistribution },
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'md',
    },
    data: {
      type: Array,
      default: null,
    },
    total: {
      type: Number,
      default: null,
    },
    chartPalette: {
      type: Array,
      default: () => [],
    },
    entity: {
      type: String,
      default: 'docs',
    },
    legendClickable: {
      type: Boolean,
      default: false,
    },
    linksExtraParams: {
      type: Object,
      default: () => {},
    },
    roundToDecimalPlaces: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return { palette, brickOver: null };
  },
  computed: {
    isCardEmpty() {
      const valuesTotalCount = this.data.reduce((total, val) => {
        return total + (val?.count || 0);
      }, 0);
      return this.data?.length < 1 || valuesTotalCount < 1;
    },
    numberOfColumns() {
      if (this.size === 'md') {
        return 1;
      }
      if (this.bricks.length <= 3) {
        return 1;
      } else if (this.bricks.length > 3 && this.bricks.length <= 6) {
        return 2;
      } else {
        return 3;
      }
    },
    itemsPerColumn() {
      if (this.size === 'md') {
        return this.bricks.length;
      }
      const allColumnsAreFilled =
        this.bricks.length % this.numberOfColumns === 0;
      const itemsPerColumnIfAllFilled = parseInt(
        this.bricks.length / this.numberOfColumns,
      );
      return allColumnsAreFilled
        ? itemsPerColumnIfAllFilled
        : itemsPerColumnIfAllFilled + 1;
    },
    columnWidth() {
      return parseInt(100 / this.numberOfColumns);
    },
    bricks() {
      return this.data.map((d, idx) => {
        const title =
          this.entity === 'docs'
            ? getDocumentTypeText(d.type)
            : getFileTypeText(d.type);

        let percentage = this.total === 0 ? 0 : (d.count / this.total) * 100;
        if (this.entity === 'file-types') {
          const typeCount = this.data.reduce((sum, i) => {
            return sum + (i.type === d.type ? d.count : 0);
          }, 0);
          const typeCountTotal = this.data.reduce((sum, i) => {
            return sum + i.count;
          }, 0);
          percentage =
            typeCountTotal === 0 ? 0 : (typeCount / typeCountTotal) * 100;
        }

        return {
          id: idx,
          title: title,
          percentage: `${percentage.toFixed(this.roundToDecimalPlaces)}%`,
          count: d.count,
          type: d.type,
          color: getPaletteColor(this.data, idx, this.chartPalette),
        };
      });
    },
  },
  methods: {
    onBrickClicked(brick) {
      navigateWithQueryParams({
        router: this.$router,
        pathname: 'documents',
        params: Object.assign(
          { return_to: 'analytics' },
          {
            ...getAndSyncQueryParams({
              fullPath: this.$route.fullPath,
              router: this.$router,
              store: this.$store,
            }),
          },
          this.entity === 'docs'
            ? { document_types: brick.type }
            : { file_types: brick.type },
          this.linksExtraParams,
        ),
      });
    },
    onBrickOver(brick) {
      this.brickOver = brick;
    },
    onBrickOut() {
      this.brickOver = null;
    },
  },
};
</script>

<template>
  <div
    :class="{ md: size === 'md', lg: size === 'lg' }"
    class="chart-card d-flex flex-column i-radius-2xl"
  >
    <div
      :class="{
        'i-mb-12': ['lg', 'full'].includes(size),
        'i-mb-8': size === 'md',
      }"
      class="header d-flex flex-column"
    >
      <div class="title i-text-neutral-120 i-text-button-2">{{ title }}</div>
      <div v-if="subtitle" class="subtitle i-text-neutral-110 i-text-body-2">
        {{ subtitle }}
      </div>
    </div>

    <div
      v-if="isCardEmpty"
      class="chart-card__empty_state d-flex flex-column align-items-center justify-content-center"
    >
      <i-icon
        symbol="large-file-text-search"
        class="icon i-text-neutral-80 i-mb-8"
        :size="46"
      ></i-icon>
      <div class="title i-text-neutral-110 i-text-button-3 i-mb-1">
        No Matching Documents
      </div>
      <div class="description i-text-neutral-110 i-text-body-3">
        Charts will appear here
      </div>
    </div>

    <div v-else class="chart-card__body d-flex flex-row align-items-center">
      <trust-score-distribution
        v-if="entity === 'trust-score'"
        class="chart-card__chart"
        :data="data"
      >
      </trust-score-distribution>
      <doughnut-chart
        v-else
        class="chart-card__chart"
        :data="data"
        :chart-palette="chartPalette"
        :size="size"
        :entity="entity"
        :total="total"
        :brick-over="brickOver"
      ></doughnut-chart>
      <div
        v-if="entity !== 'trust-score'"
        :class="{
          'flex-row': size === 'lg',
          'flex-column': size === 'md',
          'i-gap-6': size === 'lg',
          'i-gap-4': size === 'md',
        }"
        class="chart-card__legend d-flex flex-row"
      >
        <div
          v-for="(column, index) in numberOfColumns"
          :key="column"
          :class="{
            'i-gap-4': size === 'lg',
            'i-gap-2': size === 'md',
          }"
          :style="{ width: columnWidth + '%' }"
          class="chart-card__block d-flex flex-column"
        >
          <data-brick
            v-for="brick in bricks.slice(
              index * itemsPerColumn,
              index * itemsPerColumn + itemsPerColumn,
            )"
            :key="brick.id"
            :brick-data="brick"
            :size="size"
            :clickable="legendClickable"
            @brick-clicked="onBrickClicked(brick)"
            @onMouseOver="onBrickOver(brick)"
            @onMouseOut="onBrickOut(brick)"
          ></data-brick>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chart-card {
  border: 1px solid $light-neutral-60;
  width: 100%;
  padding: map-get($increments, 10);

  &__empty_state {
    min-height: 200px;
  }

  &__body {
    height: 100%;
  }

  &__legend {
    display: flex;
  }

  &.md {
    .chart-card {
      &__chart {
        width: 50%;
      }

      &__legend {
        width: 50%;
      }
    }
  }

  &.lg {
    .chart-card {
      &__chart {
        width: 33%;

        @include respond-to('small') {
          width: 40%;
        }
      }

      &__legend {
        width: 67%;

        @include respond-to('small') {
          width: 60%;
        }
      }
    }
  }
}
</style>
